import { useState, useEffect, ReactNode } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAnalytics } from "@/hooks/useAnalytics";
import NavItem from "@/components/common/NavItem";

interface AuthHeaderProps {
  bottomComponent?: ReactNode;
}

const AuthHeader = ({ bottomComponent }: AuthHeaderProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { trackNavigation } = useAnalytics();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const scrollToSection = (sectionId: string) => {
    navigate("/", { replace: true });
    // Use setTimeout to ensure navigation completes before scrolling
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
    setMobileMenuOpen(false);
  };

  // Handle header hide/show on scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY < 100) {
        // Always show header at top
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY) {
        // Scrolling down - hide header
        setIsVisible(false);
      } else {
        // Scrolling up - show header
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  return (
    <header
      className={`fixed left-0 right-0 z-50 bg-white border-b border-gray-200 transition-transform duration-300 ease-in-out ${
        isVisible ? "top-0 translate-y-0" : "-top-16 -translate-y-full"
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo/Brand */}
          <Link
            to="/"
            className="flex items-center"
            onClick={() => trackNavigation("home", "auth_header_logo")}
          >
            <img
              src="/images/logo4.png"
              alt="Wabisabi Logo"
              className="w-[100px] h-[100px]"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-[56px]">
            <NavItem
              label="Home"
              onClick={() => scrollToSection("home")}
            />
            <NavItem
              label="How it Works"
              onClick={() => scrollToSection("how-it-works")}
            />
            <NavItem
              label="Our Values"
              onClick={() => scrollToSection("our-values")}
            />
            <NavItem
              label="About"
              href="/about"
              isActive={location.pathname === "/about"}
              onClick={() => trackNavigation("about", "auth_header")}
            />
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button variant="ghost" size="icon" onClick={toggleMobileMenu}>
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <div className="container mx-auto px-4 py-2 space-y-1">
            <NavItem
              label="Home"
              onClick={() => scrollToSection("home")}
              isMobile
            />
            <NavItem
              label="How it Works"
              onClick={() => scrollToSection("how-it-works")}
              isMobile
            />
            <NavItem
              label="Our Values"
              onClick={() => scrollToSection("our-values")}
              isMobile
            />
            <NavItem
              label="About"
              href="/about"
              isActive={location.pathname === "/about"}
              onClick={() => {
                trackNavigation("about", "auth_header");
                setMobileMenuOpen(false);
              }}
              isMobile
            />
          </div>
        </div>
      )}
      
      {bottomComponent && (
        <div className="fixed left-0 right-0 z-40" style={{ top: '100px' }}>
          {bottomComponent}
        </div>
      )}
    </header>
  );
};

export default AuthHeader;
